/**
 * TrustScoreBadge Component
 * Displays trust score with color-coded badges and animated progress bar
 */

import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

interface TrustScoreBadgeProps {
  score: number; // 0-100
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

interface TrustLevel {
  color: string;
  label: string;
  gradient: string;
}

const getTrustLevel = (score: number): TrustLevel => {
  if (score >= 90) {
    return {
      color: '#10b981',
      label: 'Excelente',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    };
  } else if (score >= 70) {
    return {
      color: '#3b82f6',
      label: 'Muy bueno',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    };
  } else if (score >= 50) {
    return {
      color: '#f59e0b',
      label: 'Aceptable',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    };
  } else {
    return {
      color: '#ef4444',
      label: 'Revisar',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    };
  }
};

export const TrustScoreBadge: React.FC<TrustScoreBadgeProps> = ({
  score,
  size = 'medium',
  showLabel = true,
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const trustLevel = getTrustLevel(score);
  const normalizedScore = Math.min(100, Math.max(0, score));

  // Animate score on mount using requestAnimationFrame for smoother performance
  useEffect(() => {
    const duration = 600; // ms
    const startTime = performance.now();
    let animationFrame: number;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      const easedProgress = easeOutCubic(progress);
      
      setAnimatedScore(normalizedScore * easedProgress);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setAnimatedScore(normalizedScore);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [normalizedScore]);

  const sizeClasses = {
    small: 'ai360-trust-badge--small',
    medium: 'ai360-trust-badge--medium',
    large: 'ai360-trust-badge--large',
  };

  // Mock detailed scores (in real implementation, these would come from props)
  // Using deterministic calculations based on the main score to ensure consistency
  const technicalScore = Math.min(100, Math.max(0, score + 1));
  const contentScore = Math.min(100, Math.max(0, score - 2));
  const reputationScore = Math.min(100, Math.max(0, score));
  const coherenceScore = Math.min(100, Math.max(0, score - 1));

  return (
    <div className="ai360-trustscore-wrapper">
      <div className={clsx('ai360-trust-badge', sizeClasses[size])}>
        <div className="ai360-trust-badge-container">
          {/* Circular progress */}
          <div className="ai360-trust-badge-circle">
            <svg className="ai360-trust-badge-svg" viewBox="0 0 36 36">
              {/* Background circle */}
              <path
                className="ai360-trust-badge-bg"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                strokeWidth="2"
              />
              {/* Progress circle */}
              <path
                className="ai360-trust-badge-progress"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                strokeWidth="2"
                strokeDasharray={`${animatedScore}, 100`}
                style={{
                  stroke: trustLevel.color,
                }}
              />
            </svg>
            {/* Score number */}
            <div className="ai360-trust-badge-score">
              <span className="ai360-trust-badge-number">{Math.round(animatedScore)}</span>
            </div>
          </div>

          {/* Label */}
          {showLabel && (
            <div className="ai360-trust-badge-label" style={{ color: trustLevel.color }}>
              {trustLevel.label}
            </div>
          )}
        </div>

        {/* Linear progress bar alternative */}
        <div className="ai360-trust-badge-bar">
          <div
            className="ai360-trust-badge-bar-fill"
            style={{
              width: `${animatedScore}%`,
              background: trustLevel.gradient,
            }}
          />
        </div>
      </div>

      {/* Tooltip with detailed breakdown */}
      <div className="ai360-trustscore-tooltip">
        <h4>Análisis de Confiabilidad</h4>
        <div className="ai360-tooltip-row">
          <span>Técnico</span>
          <span>{Math.round(technicalScore)}/100</span>
        </div>
        <div className="ai360-tooltip-row">
          <span>Contenido</span>
          <span>{Math.round(contentScore)}/100</span>
        </div>
        <div className="ai360-tooltip-row">
          <span>Reputación</span>
          <span>{Math.round(reputationScore)}/100</span>
        </div>
        <div className="ai360-tooltip-row">
          <span>Coherencia</span>
          <span>{Math.round(coherenceScore)}/100</span>
        </div>
        <p className="ai360-tooltip-summary">
          {score >= 90 ? 'Fuente altamente confiable basada en SSL válido, contenido de calidad y buena reputación.' :
           score >= 70 ? 'Fuente confiable con buena reputación y contenido de calidad.' :
           score >= 50 ? 'Fuente aceptable, se recomienda verificar la información.' :
           'Se recomienda consultar fuentes adicionales para verificar la información.'}
        </p>
      </div>
    </div>
  );
};
